/**
 * This component contents a list of pill and swiper to called from GuidedNavigation
 *
 * @module views/components/PillCarousel
 * @memberof -Common
 */
import './PillCarousel.scss';
import 'swiper/swiper-bundle.min.css';
import 'swiper/swiper.min.css';

import React from 'react';

import PropTypes from 'prop-types';
import SwiperCore, {
  A11y,
  Keyboard, Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import Button from '@ulta/core/components/Button/Button';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import AppliedFilter from '@ulta/components/AppliedFilter/AppliedFilter';

import carouselUtils, { handleInactiveSwiperSlides } from '@ulta/utils/carouselUtils/carouselUtils';

import { FilterPill } from '../FilterPill/FilterPill';
import Pill from '../Pill/Pill';

SwiperCore.use( [Navigation, Keyboard, A11y] );

/**
 * Represents a PillCarousel component
 * @method
 * @param {PillCarouselProps} props - React properties passed from composition
 * @returns PillCarousel
 */

export const PillCarousel = function( props ){
  const {
    items,
    displayArrows,
    onPillClick,
    carouselAccessibility,
    previousAccessibility,
    nextAccessibility,
    carouselDetailsAccessibility,
    loading,
    onClearClick,
    clearAction,
    hasClearButton,
    pillType
  } = props;

  return (
    <div
      className='PillCarousel'
      role='region'
      aria-label={ carouselAccessibility }
    >
      <p className='sr-only'>{ carouselUtils.handleTokenizedString( carouselDetailsAccessibility ) }</p>
      { displayArrows &&
      <Button
        variant='secondary'
        iconImage='arrowBack'
        iconSize='default'
        size='tiny'
        iconOnly={ true }
        className={ `PillCarousel__Pagination--buttonback PillCarousel__Pagination--buttonback-${ pillType } PillCarousel__arrow` }
        ariaLabel={ previousAccessibility }
      />
      }
      <Swiper
        onSwiper={ ( swiper ) => handleInactiveSwiperSlides( swiper ) }
        onSlideChange={ ( swiper ) => handleInactiveSwiperSlides( swiper ) }
        watchOverflow={ true }
        watchSlidesProgress={ true }
        watchSlidesVisibility={ true }
        freeMode={ true }
        speed={ 600 }
        spaceBetween={ 8 }
        navigation={
          {
            prevEl: `.PillCarousel__Pagination--buttonback-${ pillType }`,
            nextEl: `.PillCarousel__Pagination--buttonforward-${ pillType }`,
            disabledClass: 'PillCarousel__Pagination--buttondisabled'
          }
        }
        breakpoints={
          {
            0: {
              slidesPerView: 'auto',
              slidesPerGroup: 5
            }
          }
        }
      >
        { hasItems( items ) && items.map( ( item, index ) => {
          const type = item.pillType || pillType;
          return (
            <SwiperSlide key={ index }
              className='SwiperWidth'
            >
              { PILL_TYPES.FILTER === type && <FilterPill { ...spreadProps( { ...item } ) } /> }

              { PILL_TYPES.PILL === type && (
                <Pill
                  key={ item.name }
                  message={ item.name }
                  onClick={ onPillClick || item.onPillClick }
                  selectAction={ item.selectAction }
                />
              ) }

              { PILL_TYPES.DISMISSIBLE === type && (
                <AppliedFilter
                  itemText={ item.title || item.label }
                  key={ item.id }
                  colorCode={ item.colorCode }
                  loading={ loading }
                  colorImage={ item.colorImage }
                  onClick={ () => onPillClick( item ) }
                  ariaLabel={ item.removeFilterAccessibility }
                  rating={ item.id }
                  groupType={ item.groupType }
                />
              ) }
            </SwiperSlide>
          );
        } ) }
        { hasClearButton &&
          <SwiperSlide className='PillCarousel--ClearButton SwiperWidth'>
            <span className=''
              data-test='reset-btn-mobile'
            >
              <Button
                action={ clearAction }
                likeLink
                label={ clearAction.label }
                onClick={ onClearClick }
                disabled={ loading }
              />
            </span>
          </SwiperSlide>
        }
      </Swiper>
      { displayArrows &&
        <Button
          variant='secondary'
          iconImage='arrowForward'
          iconSize='default'
          size='tiny'
          iconOnly={ true }
          className={ `PillCarousel__Pagination--buttonforward PillCarousel__Pagination--buttonforward-${ pillType } PillCarousel__arrow` }
          ariaLabel={ nextAccessibility }
        />
      }
    </div>
  );
};

/**
 * Pill types
 * @constant {object}
 * @default
 */
export const PILL_TYPES = {
  PILL: 'PILL',
  DISMISSIBLE: 'DISMISSIBLE',
  FILTER: 'FILTER'
};

/**
  * Property type definitions
  * @typedef PillCarouselProps
  * @type {object}
  * @property {array} items - This is array of items
  * @property {boolean} displayArrows - flag to display swiper Pagination
  * @property {function} onPillClick - function to handle on pill click
  * @property {string} carouselAccessibility - This is the ADA text for carousel
  * @property {string} previousAccessibility - This is the ADA text for carousel prev pagination button
  * @property {string} nextAccessibility - This is the ADA text for carousel next pagination button
  */

export const propTypes =  {
  items: PropTypes.arrayOf( PropTypes.shape(
    {
      name: PropTypes.string,
      selected: PropTypes.bool,
      selectAction: PropTypes.shape( {
        label: PropTypes.string,
        url: PropTypes.string,
        graphql: PropTypes.string
      } )
    }
  ) ),
  displayArrows: PropTypes.bool,
  onPillClick: PropTypes.func,
  carouselAccessibility: PropTypes.string,
  previousAccessibility: PropTypes.string,
  nextAccessibility: PropTypes.string
};


PillCarousel.propTypes = propTypes;

export default PillCarousel;
